/**
 * Минимальный fetch-обёртка для wiki API.
 *
 * В dev: vite proxy перенаправляет /api → localhost:3000.
 * В prod: nginx на wiki.brasol.ru проксирует /api на тот же backend
 * что и CRM (см. deploy/nginx-wiki.conf).
 *
 * Cookies — credentials: 'include'. Session-cookie с domain=.brasol.ru
 * (когда юзер добавит COOKIE_DOMAIN на VM) разделяется между CRM и wiki.
 */

import { withPreviewParam } from './preview';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  // Вкладка предпросмотра черновика: запросы ученика — с ?preview=1.
  const r = await fetch(`/api${withPreviewParam(path)}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    let parsed: unknown = null;
    try {
      parsed = await r.json();
    } catch {
      // not JSON
    }
    const msg =
      (parsed && typeof parsed === 'object' && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : null) || `HTTP ${r.status}`;
    throw new ApiError(r.status, parsed, msg);
  }
  // 204 No Content (например, DELETE /auth/view-as) — тела нет, json() упал бы.
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

/**
 * Повтор при кратком сбое сервера (06.10.2026: у процесса API на 10–20 с
 * кончились соединения с БД — «ИИ: из текста» упал с 500, повтор через 3 с
 * прошёл). Повторяем только 5xx и обрыв сети; 4xx — сразу наружу.
 */
export async function retryTransient<T>(fn: () => Promise<T>, delaysMs: readonly number[] = [2500, 6000]): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (e) {
      const transient = e instanceof ApiError ? e.status >= 500 : e instanceof TypeError;
      if (!transient || i >= delaysMs.length) throw e;
      await new Promise((r) => setTimeout(r, delaysMs[i]));
    }
  }
}

/** Текст ошибки для окна: 5xx — понятной фразой вместо «Internal server error». */
export function friendlyError(e: unknown): string {
  if (e instanceof ApiError && e.status >= 500) return 'Сервер на минуту перегружен — нажмите ещё раз. Текст не потерян.';
  if (e instanceof TypeError) return 'Нет связи с сервером — проверьте интернет и нажмите ещё раз.';
  return (e as Error).message;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * POST с бинарным ответом (например .docx). Возвращает Blob. Кука сессии
 * едет автоматически (credentials: include). Ошибки — пытаемся вытащить
 * JSON-сообщение, иначе HTTP-статус.
 */
export async function httpBlob(
  path: string,
  body: unknown,
  method = 'POST',
): Promise<Blob> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      msg = (data as { message?: string }).message ?? msg;
    } catch {
      /* тело не JSON — оставляем статус */
    }
    throw new Error(msg);
  }
  return res.blob();
}
